class TabbedContentBlock {
  constructor() {
    this.sections = this.findSections();
    if (!this.sections.length) return;

    this.sections.forEach((section, sectionIndex) => {
      this.initSection(section, sectionIndex);
    });
  }

  findSections() {
    const explicit = Array.from(
        document.querySelectorAll(".tabbed-content, .ktg-tabbed-content")
    );

    const inferred = Array.from(
        document.querySelectorAll(".tabbed-content__tablist")
    )
        .map((tabList) => {
          const root = tabList.closest(".tabbed-content, .ktg-tabbed-content");
          if (root) return root;

          const inner =
              tabList.closest(".tabbed-content__inner") ||
              tabList.closest(".abbed-content__inner");
          if (inner) return inner;

          return tabList.parentElement;
        })
        .filter(Boolean);

    return Array.from(new Set([...explicit, ...inferred])).filter((section) => {
      return (
          section.querySelector(".tabbed-content__tablist") &&
          section.querySelector(".tabbed-content__panels")
      );
    });
  }

  initSection(section, sectionIndex) {
    const tabList = section.querySelector(".tabbed-content__tablist");
    const panelsWrap = section.querySelector(".tabbed-content__panels");
    if (!tabList || !panelsWrap) return;

    const tabs = this.getTabs(tabList);
    const panels = this.getPanels(panelsWrap);
    const pairCount = Math.min(tabs.length, panels.length);
    if (!pairCount) return;

    const pairedTabs = tabs.slice(0, pairCount);
    const pairedPanels = panels.slice(0, pairCount);
    tabList.setAttribute("role", "tablist");

    this.applyA11y(pairedTabs, pairedPanels, sectionIndex);
    this.markPanelColumnRoles(pairedPanels);
    this.bindTabEvents(pairedTabs, pairedPanels);
    this.mountScrollArrows(section, tabList);

    const activeTab = pairedTabs.find((tab) => tab.classList.contains("is-active"));
    this.activateTab(activeTab || pairedTabs[0], pairedTabs, pairedPanels, null, false, false);

    // Keep behavior in sync when crossing mobile/desktop breakpoints.
    window.addEventListener("resize", () => {
      const currentActive = pairedTabs.find((tab) => tab.classList.contains("is-active"));
      this.activateTab(currentActive || pairedTabs[0], pairedTabs, pairedPanels, null, false, false);
    });
  }

  getTabs(tabList) {
    return Array.from(tabList.children).filter(
        (item) => item.tagName && item.tagName.toLowerCase() === "li"
    );
  }

  getPanels(panelsWrap) {
    return Array.from(panelsWrap.children).filter((item) =>
        item.classList.contains("wp-block-columns")
    );
  }

  applyA11y(tabs, panels, sectionIndex) {
    tabs.forEach((tab, index) => {
      tab.classList.add("tabbed-content__tab");
      tab.setAttribute("role", "tab");
      tab.setAttribute("tabindex", "-1");
      tab.setAttribute("aria-selected", "false");

      const panelId =
          panels[index].id || `tabbed-content-panel-${sectionIndex + 1}-${index + 1}`;
      panels[index].id = panelId;

      const tabId = tab.id || `tabbed-content-tab-${sectionIndex + 1}-${index + 1}`;
      tab.id = tabId;
      tab.setAttribute("aria-controls", panelId);
    });

    panels.forEach((panel, index) => {
      panel.classList.add("tabbed-content__panel");
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("hidden", "hidden");
      panel.classList.remove("is-active");
      panel.setAttribute("aria-labelledby", tabs[index].id);
    });
  }

  markPanelColumnRoles(panels) {
    panels.forEach((panel) => {
      const columns = Array.from(panel.children).filter((item) =>
          item.classList.contains("wp-block-column")
      );

      columns.forEach((column) => {
        column.classList.remove(
            "tabbed-content__panel-col--media",
            "tabbed-content__panel-col--content"
        );

        const hasImage = !!column.querySelector(".wp-block-image");
        column.classList.add(
            hasImage ? "tabbed-content__panel-col--media" : "tabbed-content__panel-col--content"
        );
      });
    });
  }

  bindTabEvents(tabs, panels) {
    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => {
        this.activateTab(tab, tabs, panels, null, false, true);
      });

      tab.addEventListener("keydown", (event) => {
        let nextIndex = index;

        if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
        if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;

        if (nextIndex !== index) {
          event.preventDefault();
          this.activateTab(tabs[nextIndex], tabs, panels, null, true, true);
        }
      });
    });
  }

  activateTab(tab, tabs, panels, mobileSelect = null, moveFocus = false, scrollToPanel = false) {
    const targetIndex = tabs.indexOf(tab);
    if (targetIndex < 0) return;
    const isMobile = this.isMobileMode();

    tabs.forEach((item, index) => {
      const isActive = index === targetIndex;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-selected", isActive ? "true" : "false");
      item.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    if (isMobile) {
      panels.forEach((panel) => {
        panel.classList.add("is-active");
        panel.removeAttribute("hidden");
      });

      if (scrollToPanel && panels[targetIndex]) {
        panels[targetIndex].scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      panels.forEach((panel, index) => {
        const isActive = index === targetIndex;
        panel.classList.toggle("is-active", isActive);
        if (isActive) panel.removeAttribute("hidden");
        else panel.setAttribute("hidden", "hidden");
      });
    }

    if (mobileSelect) mobileSelect.value = String(targetIndex);
    if (moveFocus) tab.focus();
  }

  isMobileMode() {
    return window.matchMedia("(max-width: 781px)").matches;
  }

  mountScrollArrows(section, tabList) {
    const tabShell = section.querySelector(".tabbed-content__tabs-shell");
    if (!tabShell) return;

    let leftArrow = tabShell.querySelector(".tabbed-content__scroll-arrow--left");
    let rightArrow = tabShell.querySelector(".tabbed-content__scroll-arrow--right");

    if (!leftArrow) {
      leftArrow = document.createElement("button");
      leftArrow.type = "button";
      leftArrow.className = "tabbed-content__scroll-arrow tabbed-content__scroll-arrow--left";
      leftArrow.setAttribute("aria-label", "Scroll tabs left");
      leftArrow.innerHTML = "&#10094;";
      tabShell.prepend(leftArrow);
    }

    if (!rightArrow) {
      rightArrow = document.createElement("button");
      rightArrow.type = "button";
      rightArrow.className = "tabbed-content__scroll-arrow tabbed-content__scroll-arrow--right";
      rightArrow.setAttribute("aria-label", "Scroll tabs right");
      rightArrow.innerHTML = "&#10095;";
      tabShell.append(rightArrow);
    }

    const updateArrows = () => {
      const canScroll = tabList.scrollWidth > tabList.clientWidth + 2;
      const atStart = tabList.scrollLeft <= 2;
      const atEnd = tabList.scrollLeft + tabList.clientWidth >= tabList.scrollWidth - 2;

      tabShell.classList.toggle("is-scrollable", canScroll);
      leftArrow.disabled = !canScroll || atStart;
      rightArrow.disabled = !canScroll || atEnd;
    };

    leftArrow.addEventListener("click", () => {
      tabList.scrollBy({ left: -220, behavior: "smooth" });
    });

    rightArrow.addEventListener("click", () => {
      tabList.scrollBy({ left: 220, behavior: "smooth" });
    });

    tabList.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);
    updateArrows();
  }
}

export default TabbedContentBlock;
